import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ApiError, getApiClient } from '@signalvote/api-client';
import { ErrorCode, isStaleWrite } from '@signalvote/shared';
import type {
  ImportReattributionResult,
  ReattributeImportRequest,
  UndoImportRequest,
  UndoImportResult,
} from '@signalvote/shared';
import { authService } from '@/services/authService';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

// Import history, undo, and moving a bank-file import to another account. Undo and move
// both send the batch's token from its history row, so a refusal for a stale token reloads
// the history and the next try carries the fresh one.

/** What a refused undo or move means for the treasurer. */
export type ImportBatchRefusal =
  | { kind: 'changedElsewhere' }
  | { kind: 'undoBlocked'; reasons: string[] }
  | { kind: 'periodLocked'; message: string; reportIds: string[] }
  | { kind: 'other'; message: string };

export function importBatchRefusal(error: unknown): ImportBatchRefusal {
  if (isStaleWrite(error)) return { kind: 'changedElsewhere' };
  if (error instanceof ApiError) {
    switch (error.code) {
      case ErrorCode.UndoImportBlockedByDownstreamActions:
        return { kind: 'undoBlocked', reasons: stringList(error.body.blockingReasons) };
      case ErrorCode.ImportReattributeFiledReportPresent:
        return { kind: 'periodLocked', message: error.message, reportIds: stringList(error.body.affectedReportIds) };
      case null:
        break;
    }
  }
  return { kind: 'other', message: error instanceof Error ? error.message : String(error) };
}

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function reloadHistoryWhenStale(queryClient: QueryClient) {
  return (error: unknown) => {
    if (importBatchRefusal(error).kind === 'changedElsewhere')
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.importBatches.all });
  };
}

export function useImportBatchHistory(limit: number = 50) {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.finance.importBatches.history(limit),
    queryFn: async () => (await getApiClient().importBatch.getHistory({ limit })).items,
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

export function useUndoPreview(batchId: string | null) {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.finance.importBatches.undoPreview(batchId ?? ''),
    queryFn: () => getApiClient().importBatch.getUndoPreview(batchId!),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!batchId,
  });
}

export function useUndoImport() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    UndoImportResult,
    Error,
    { batchId: string; request: UndoImportRequest }
  >({
    mutationFn: ({ batchId, request }) => getApiClient().importBatch.undo(batchId, request),
    onSuccess: () => {
      // The cascade deletes ledger, bank and expenditure rows and recomputes aggregates
      // and flags server-side, so every surface that reads those rows refetches.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.importBatches.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.reconciliation.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.contributions.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.expenditures.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.health() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
    onError: reloadHistoryWhenStale(queryClient),
  });

  return { undoImport: mutateAsync, isPending };
}

export function useReattributeImport() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    ImportReattributionResult,
    Error,
    { batchId: string; request: ReattributeImportRequest }
  >({
    mutationFn: ({ batchId, request }) => getApiClient().importBatch.reattribute(batchId, request),
    onSuccess: () => {
      // A move changes only which account the batch and its bank lines sit on; the
      // close ledger shows that account on each line.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.importBatches.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankTransactions.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankIngestion.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.sortInboxAll() });
    },
    onError: reloadHistoryWhenStale(queryClient),
  });

  return { reattributeImport: mutateAsync, isPending };
}
